// src/components/Layout.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/store";
import { logout } from "../redux/slices/authSlice";
import { toggleTheme } from "../redux/slices/themeSlice";
import {
  getUnreadChatCount,
  addMessage,
  setCurrentRecovery,
} from "../redux/slices/recoveryChatSlice";
import { GlobalStyles } from "../styles/globalStyles";
import { pusherService } from "../services/api";
import { isAdmin } from "../types";
import logo from "../assets/images/logo.png";
import {
  Home,
  Wallet as WalletIcon,
  CreditCard,
  Shield,
  User,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Lock,
  Briefcase,
  ChevronDown,
  Info,
  MessageSquare,
  AlertTriangle,
  BarChart3,
} from "lucide-react";
import toast from "react-hot-toast";

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );
  const { mode } = useSelector((state: RootState) => state.theme);
  const { unreadCount } = useSelector(
    (state: RootState) => state.recoveryChats,
  );

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    "disconnected" | "connecting" | "connected" | "error"
  >("disconnected");
  const [pusherInitialized, setPusherInitialized] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const initializationRef = useRef<boolean>(false);
  const tokenRef = useRef<string | null>(null);
  const mountedRef = useRef<boolean>(true);
  const subscribeToRecoveryChannelsRef = useRef<(() => void) | null>(null);
  const currentChatRef = useRef<{ caseId: number; isAdmin: boolean } | null>(
    null,
  );
  const lastAuthCheckRef = useRef<number>(0);
  const initializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isUserAdmin = user ? isAdmin(user) : false;

  const navigation = [
    { name: "Dashboard", path: "/dashboard", icon: Home, key: "dashboard" },
    { name: "Wallet", path: "/wallet", icon: WalletIcon, key: "wallet" },
    { name: "Deposit", path: "/deposit", icon: ArrowDownRight, key: "deposit" },
    {
      name: "Withdraw",
      path: "/withdraw",
      icon: ArrowUpRight,
      key: "withdraw",
    },
    {
      name: "Transactions",
      path: "/transactions",
      icon: CreditCard,
      key: "transactions",
    },
    { name: "Recovery", path: "/recovery", icon: Shield, key: "recovery" },
    { name: "Profile", path: "/profile", icon: User, key: "profile" },
    ...(isUserAdmin
      ? [
          {
            name: "Admin Dashboard",
            path: "/admin",
            icon: BarChart3,
            key: "admin-dashboard",
          },
          {
            name: "Users",
            path: "/admin/users",
            icon: Users,
            key: "admin-users",
          },
          {
            name: "Transactions",
            path: "/admin/transactions",
            icon: CreditCard,
            key: "admin-transactions",
          },
          { name: "KYC", path: "/admin/kyc", icon: Lock, key: "admin-kyc" },
          {
            name: "Recovery Cases",
            path: "/admin/recovery",
            icon: Shield,
            key: "admin-recovery",
          },
        ]
      : []),
  ];

  useEffect(() => {
    const handleChatOpen = (event: CustomEvent) => {
      const { caseId, isAdmin } = event.detail;
      currentChatRef.current = { caseId, isAdmin };
      dispatch(setCurrentRecovery(caseId));
    };

    const handleChatClose = () => {
      currentChatRef.current = null;
      dispatch(setCurrentRecovery(null));
    };

    window.addEventListener("chat-open" as any, handleChatOpen);
    window.addEventListener("chat-close" as any, handleChatClose);

    return () => {
      window.removeEventListener("chat-open" as any, handleChatOpen);
      window.removeEventListener("chat-close" as any, handleChatClose);
    };
  }, [dispatch]);

  useEffect(() => {
    subscribeToRecoveryChannelsRef.current = () => {
      if (!user || !pusherService.getInstance() || !mountedRef.current) return;

      try {
        if (isUserAdmin) {
          const adminChannel = pusherService.subscribeToChannel(
            "private-admin.recovery",
          );

          if (adminChannel) {
            adminChannel.bind("pusher:subscription_succeeded", () => {
              console.log("✅ Connected to admin.recovery");
            });

            adminChannel.bind("RecoveryMessageSent", (data: any) => {
              if (!data?.chat) return;
              const chat = data.chat;

              dispatch(getUnreadChatCount() as any);
              const currentChat = currentChatRef.current;
              const isForCurrentChat =
                currentChat?.caseId === chat.recovery_request_id;

              if (isForCurrentChat) {
                window.dispatchEvent(
                  new CustomEvent("pusher-message-received", {
                    detail: {
                      message: chat,
                      caseId: chat.recovery_request_id,
                    },
                  }),
                );
              }

              dispatch(addMessage(chat));
            });
          }
        } else {
          const userChannel = pusherService.subscribeToChannel(
            `private-user.recovery.${user.id}`,
          );

          if (userChannel) {
            userChannel.bind("pusher:subscription_succeeded", () => {
              console.log(`✅ Connected to user.recovery.${user.id}`);
            });

            userChannel.bind("RecoveryMessageSent", (data: any) => {
              if (!data?.chat) return;
              const chat = data.chat;

              dispatch(getUnreadChatCount() as any);
              const currentChat = currentChatRef.current;
              const isForCurrentChat =
                currentChat?.caseId === chat.recovery_request_id;

              if (isForCurrentChat) {
                window.dispatchEvent(
                  new CustomEvent("pusher-message-received", {
                    detail: {
                      message: chat,
                      caseId: chat.recovery_request_id,
                    },
                  }),
                );
              }

              dispatch(addMessage(chat));
            });
          }
        }
      } catch (error) {
        console.error("❌ Error subscribing to channel:", error);
      }
    };
  }, [user, isUserAdmin, dispatch]);

  const initializePusher = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!isAuthenticated || !user || !token) return;

    if (
      initializationRef.current &&
      tokenRef.current === token &&
      pusherService.getInstance()
    ) {
      return;
    }

    if (initializationTimeoutRef.current) {
      clearTimeout(initializationTimeoutRef.current);
      initializationTimeoutRef.current = null;
    }

    const now = Date.now();
    if (now - lastAuthCheckRef.current < 1000) return;
    lastAuthCheckRef.current = now;

    initializationRef.current = true;
    tokenRef.current = token;
    setConnectionStatus("connecting");

    try {
      const existingInstance = pusherService.getInstance();
      if (existingInstance && tokenRef.current !== token) {
        pusherService.disconnect();
      }

      const pusher = pusherService.initialize(token);

      pusher.connection.bind("connected", () => {
        setConnectionStatus("connected");
        setRetryCount(0);
        setPusherInitialized(true);

        setTimeout(() => {
          if (subscribeToRecoveryChannelsRef.current && mountedRef.current) {
            subscribeToRecoveryChannelsRef.current();
          }
        }, 500);
      });

      pusher.connection.bind("error", (error: any) => {
        setConnectionStatus("error");
        setPusherInitialized(false);
        initializationRef.current = false;

        setTimeout(() => {
          if (mountedRef.current && isAuthenticated) {
            initializationRef.current = false;
            initializePusher();
          }
        }, 3000);
      });

      pusher.connection.bind("disconnected", () => {
        setConnectionStatus("disconnected");
        setPusherInitialized(false);
        initializationRef.current = false;
      });

      pusher.connection.bind("connecting", () => {
        setConnectionStatus("connecting");
      });
    } catch (error) {
      setConnectionStatus("error");
      initializationRef.current = false;
      setPusherInitialized(false);
    }
  }, [isAuthenticated, user]);

  const cleanupPusher = useCallback(() => {
    if (initializationTimeoutRef.current) {
      clearTimeout(initializationTimeoutRef.current);
      initializationTimeoutRef.current = null;
    }

    pusherService.disconnect();
    setConnectionStatus("disconnected");
    setPusherInitialized(false);
    initializationRef.current = false;
    tokenRef.current = null;
  }, []);

  const handleLogout = () => {
    cleanupPusher();
    dispatch(logout() as any);
    navigate("/login");
  };

  useEffect(() => {
    if (!user) return;

    const fetchUnreadCount = () => {
      dispatch(getUnreadChatCount() as any);
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [dispatch, user]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  useEffect(() => {
    if (isAuthenticated && user && !pusherInitialized && mountedRef.current) {
      initializationTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          initializePusher();
        }
      }, 500);
    } else if (!isAuthenticated && pusherInitialized) {
      cleanupPusher();
    }

    return () => {
      mountedRef.current = false;
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }
    };
  }, [isAuthenticated, user, initializePusher, cleanupPusher]);

  const handleRetryConnection = () => {
    setRetryCount((prev) => prev + 1);
    if (retryCount >= maxRetries) {
      toast.error("Max retries reached. Please refresh the page.");
      return;
    }
    initializationRef.current = false;
    setPusherInitialized(false);
    initializePusher();
  };

  if (
    !isAuthenticated &&
    ![
      "/login",
      "/register",
      "/verify-email",
      "/forgot-password",
      "/reset-password",
    ].includes(location.pathname)
  ) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-gray-100/50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-800/50">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 z-40 h-screen w-72 border-r border-gray-200/20 dark:border-gray-800/30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-2xl hidden lg:flex flex-col">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-primary/5 opacity-20 pointer-events-none"></div>

        {/* Logo Section - Fixed at top */}
        <div className="relative flex-shrink-0 flex h-20 items-center justify-center border-b border-gray-200/20 dark:border-gray-800/30 px-6">
          <Link to="/dashboard" className="flex items-center gap-3 group/logo">
            <div className="relative">
              <img
                src={logo}
                alt="ObeliskRecovery"
                className="h-16 w-auto transition-all duration-500 group-hover/logo:scale-110 group-hover/logo:rotate-3"
              />
              <div className="absolute -top-1 -right-1 h-4 w-4 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full animate-pulse border-2 border-white dark:border-gray-900"></div>
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                Pelivor Recovery
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Secure Asset Management
              </span>
            </div>
          </Link>
        </div>

        {/* User Info - Fixed below logo */}
        <div className="relative flex-shrink-0 p-6 border-b border-gray-200/20 dark:border-gray-800/30">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div
                className={`absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-white dark:border-gray-900 ${
                  connectionStatus === "connected"
                    ? "bg-emerald-400"
                    : connectionStatus === "connecting"
                      ? "bg-amber-400 animate-pulse"
                      : "bg-gray-400"
                }`}
              ></div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 dark:text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable Navigation Area */}
        <div className="flex-1 overflow-y-auto">
          <nav className="p-4">
            <div className="space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.path ||
                  (item.path !== "/dashboard" &&
                    location.pathname.startsWith(item.path));

                const showUnreadCount =
                  (isUserAdmin &&
                    item.path.includes("recovery") &&
                    unreadCount > 0) ||
                  (!isUserAdmin && item.name === "Recovery" && unreadCount > 0);

                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    className={`group/nav flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-all duration-300 relative overflow-hidden ${
                      isActive
                        ? "bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 text-primary shadow-sm"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 hover:text-primary"
                    }`}
                  >
                    {/* Active indicator */}
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-1.5 bg-gradient-to-b from-primary to-primary-400 rounded-full"></div>
                    )}

                    <Icon
                      className={`h-5 w-5 transition-transform group-hover/nav:scale-110 ${
                        isActive
                          ? "text-primary"
                          : "text-gray-400 dark:text-gray-500 group-hover/nav:text-primary"
                      }`}
                    />
                    <span className="font-medium flex-1">{item.name}</span>

                    {/* Badges */}
                    {item.name.includes("Admin") && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-500/10 to-purple-600/10 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
                        Admin
                      </span>
                    )}

                    {showUnreadCount && (
                      <span className="px-2 py-1 text-xs font-bold rounded-full bg-gradient-to-r from-rose-500 to-rose-600 text-white min-w-6 flex items-center justify-center animate-pulse">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Fixed Footer at Bottom */}
        <div className="flex-shrink-0 border-t border-gray-200/20 dark:border-gray-800/30">
          {/* Connection Status */}
          <div className="p-4 border-b border-gray-200/20 dark:border-gray-800/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div
                  className={`h-2 w-2 rounded-full ${
                    connectionStatus === "connected"
                      ? "bg-emerald-400 animate-pulse"
                      : connectionStatus === "connecting"
                        ? "bg-amber-400 animate-ping"
                        : "bg-gray-400"
                  }`}
                ></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {connectionStatus === "connected"
                    ? "Live Connection"
                    : connectionStatus === "connecting"
                      ? "Connecting..."
                      : "Offline"}
                </span>
              </div>
              {connectionStatus === "error" && retryCount < maxRetries && (
                <button
                  onClick={handleRetryConnection}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} !px-3`}
                >
                  Retry
                </button>
              )}
            </div>
            {unreadCount > 0 && (
              <div className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-gradient-to-r from-primary/5 to-primary/10">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-primary" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Unread messages
                  </span>
                </div>
                <span className="text-sm font-bold text-primary">
                  {unreadCount}
                </span>
              </div>
            )}
          </div>

          {/* Logout Button */}
          <div className="p-4">
            <button
              onClick={handleLogout}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonMedium} w-full bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-700 dark:hover:to-gray-800 !text-gray-700 dark:!text-gray-300 group/logout`}
            >
              <LogOut className="h-5 w-5 mr-2 transition-transform group-hover/logout:-translate-x-1" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="fixed left-0 top-0 z-50 h-screen w-80 border-r border-gray-200/30 dark:border-gray-800/30 bg-white dark:bg-gray-900 backdrop-blur-2xl lg:hidden flex flex-col">
            <div className="flex-shrink-0 flex h-20 items-center justify-between border-b border-gray-200/30 dark:border-gray-800/30 px-6">
              <Link to="/dashboard" className="flex items-center gap-3">
                <img src={logo} alt="Pelivor" className="h-12 w-auto" />
                <span className="text-lg font-bold text-primary">Pelivor</span>
              </Link>
              <button
                onClick={() => setSidebarOpen(false)}
                className={`${GlobalStyles.iconButton}`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <nav className="p-4">
                <div className="space-y-1">
                  {navigation.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    const showUnreadCount =
                      (isUserAdmin &&
                        item.path.includes("recovery") &&
                        unreadCount > 0) ||
                      (!isUserAdmin &&
                        item.name === "Recovery" &&
                        unreadCount > 0);

                    return (
                      <Link
                        key={item.key}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                          isActive
                            ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                        <span className="font-medium flex-1">{item.name}</span>
                        {showUnreadCount && (
                          <span className="px-2 py-1 text-xs font-bold rounded-full bg-gradient-to-r from-rose-500 to-rose-600 text-white min-w-6 flex items-center justify-center animate-pulse">
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </nav>
            </div>

            <div className="flex-shrink-0 p-4 border-t border-gray-200/30 dark:border-gray-800/30">
              <div className="mb-4 p-3 rounded-xl bg-gradient-to-br from-gray-50/50 to-gray-100/50 dark:from-gray-900/30 dark:to-gray-800/30">
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className={`h-2 w-2 rounded-full ${
                      connectionStatus === "connected"
                        ? "bg-emerald-400 animate-pulse"
                        : connectionStatus === "connecting"
                          ? "bg-amber-400 animate-ping"
                          : "bg-gray-400"
                    }`}
                  ></div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {connectionStatus}
                  </span>
                </div>
                {unreadCount > 0 && (
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {unreadCount} unread message{unreadCount !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  handleLogout();
                  setSidebarOpen(false);
                }}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonMedium} w-full`}
              >
                <LogOut className="h-5 w-5 mr-2" />
                <span>Sign Out</span>
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main Content */}
      <div className="lg:pl-72">
        {/* Top Header */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-gray-200/20 dark:border-gray-800/30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`${GlobalStyles.iconButton} lg:hidden`}
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Breadcrumb */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                <span className="capitalize">
                  {location.pathname.split("/")[1] || "Dashboard"}
                </span>
                {location.pathname.split("/")[2] && (
                  <>
                    <ChevronDown className="h-3 w-3 rotate-270" />
                    <span className="text-gray-900 dark:text-white">
                      {location.pathname.split("/")[2]}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={() => dispatch(toggleTheme())}
              className={`${GlobalStyles.iconButton} hover:bg-primary/10`}
            >
              {mode === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>

            {/* Notifications */}
            {unreadCount > 0 && (
              <Link
                to={isUserAdmin ? "/admin/recovery" : "/recovery"}
                className="relative p-2 rounded-lg hover:bg-primary/10 transition-colors"
              >
                <MessageSquare className="h-5 w-5 text-primary" />
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-gradient-to-r from-rose-500 to-rose-600 text-xs text-white flex items-center justify-center">
                  {unreadCount}
                </span>
              </Link>
            )}

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <ChevronDown className="h-4 w-4 text-gray-500" />
              </button>

              {mobileMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {user?.email}
                    </p>
                  </div>
                  {isUserAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <Briefcase className="h-4 w-4" />
                      Admin Panel
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <User className="h-4 w-4" />
                    Profile Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 w-full text-left"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-6">
          {/* Status Alerts */}
          {user?.display_message && (
            <div className="mb-6">
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} !bg-gradient-to-r !from-primary/5 !to-primary/10 !border-primary/20`}
              >
                <Info className="h-5 w-5 text-primary flex-shrink-0" />
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {user.display_message}
                </p>
              </div>
            </div>
          )}

          {connectionStatus === "error" && (
            <div className="mb-6">
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertWarning}`}
              >
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Connection Error</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {retryCount < maxRetries
                      ? `Attempt ${retryCount + 1} of ${maxRetries}`
                      : "Please refresh the page"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleRetryConnection}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonSmall}`}
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          )}

          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
